// Sheet — iOS HIG bottom sheet / dialog system
// Variants: sheet (bottom slide-up), dialog (centered modal), fullscreen
// Smooth spring animations, drag handle, safe area aware

import { ReactNode, useEffect } from 'react';

export type SheetVariant = 'sheet' | 'dialog' | 'fullscreen';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  variant?: SheetVariant;
  title?: string;
  children: ReactNode;
  testId?: string;
}

export function Sheet({ open, onClose, variant = 'sheet', title, children, testId = 'sheet' }: SheetProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  if (variant === 'dialog') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={`${testId}-dialog`}>
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
        <div className="relative w-full max-w-[420px] rounded-[20px] bg-white ios26-card" data-testid={testId}>
          {title && <h2 className="px-5 pt-5 text-[20px] font-bold text-[#1C1C1E] tracking-tight">{title}</h2>}
          <div className="p-5">{children}</div>
        </div>
      </div>
    );
  }

  if (variant === 'fullscreen') {
    return (
      <div className="fixed inset-0 z-50 bg-white" data-testid={testId}>
        <div className="flex h-11 items-center justify-between border-b border-[#E5E5EA] bg-white/80 backdrop-blur-xl px-2">
          <button onClick={onClose} data-testid={`${testId}-close`} className="flex h-9 w-9 items-center justify-center text-[#007AFF] haptic">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M6 6l12 12M6 18L18 6" strokeLinecap="round" /></svg>
          </button>
          {title && <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">{title}</div>}
          <div className="w-9" />
        </div>
        <div className="overflow-y-auto" style={{ height: 'calc(100vh - 44px)' }}>{children}</div>
      </div>
    );
  }

  // Default: bottom sheet
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" data-testid={`${testId}-wrap`}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative w-full max-w-[420px] rounded-t-[20px] bg-white sheet-spring"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        data-testid={testId}
      >
        <div className="flex justify-center pt-2.5 pb-1">
          <div className="h-1 w-9 rounded-full bg-[#E5E5EA]" />
        </div>
        {title && <h2 className="px-4 pb-2 text-[20px] font-bold tracking-tight text-[#1C1C1E]">{title}</h2>}
        <div className="px-4 pb-4">{children}</div>
      </div>
    </div>
  );
}
